/**
 * Singleton script loader for CatholicAdnet display tags.
 * Safe for SPA navigation — loads the loader script once per URL.
 *
 * Loader URL returns a bootstrap that injects adloadbmd.min.js. ensureScript
 * resolves when CANBMDRunADUnit is ready (or bounded timeout). Mount activates
 * on the first non-zero geometry sample — one rAF when needed, then a short
 * cancelled-on-unmount poll. Inventory width/height drive aspect ratio only;
 * CSS display size is the available container width.
 */

import "./CatholicAdnetAdapter.css";

var loadedScripts = {};

function normalizeLoaderUrl(url) {
  if (!url) return "";
  var u = String(url).trim();
  if (!u) return "";
  return u.charAt(u.length - 1) === "/" ? u : u + "/";
}

function canbmdReady() {
  return (
    typeof window !== "undefined" &&
    typeof window.CANBMDRunADUnit === "function"
  );
}

/**
 * Bounded wait for the CatholicAdnet runtime API after the loader tag exists.
 * Immediate-first; 50ms poll; resolves even on timeout so mount can fall back
 * to a short activation poll instead of hanging forever.
 *
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
function waitForCanbmdRuntime(timeoutMs) {
  var limit = typeof timeoutMs === "number" && timeoutMs > 0 ? timeoutMs : 4000;
  return new Promise(function (resolve) {
    if (canbmdReady()) {
      resolve();
      return;
    }
    var elapsed = 0;
    var intervalMs = 50;
    var timer = window.setInterval(function () {
      elapsed += intervalMs;
      if (canbmdReady() || elapsed >= limit) {
        window.clearInterval(timer);
        resolve();
      }
    }, intervalMs);
  });
}

/**
 * @param {string} loaderUrl
 * @returns {Promise<void>}
 */
export function ensureCatholicAdnetScript(loaderUrl) {
  var src = normalizeLoaderUrl(loaderUrl);
  if (!src) {
    return Promise.reject(new Error("catholicadnet_loader_missing"));
  }
  if (loadedScripts[src]) {
    return loadedScripts[src];
  }

  loadedScripts[src] = new Promise(function (resolve, reject) {
    function afterTagReady() {
      waitForCanbmdRuntime(4000).then(resolve);
    }

    var existing = document.querySelector(
      'script[data-tpg-catholicadnet-loader="' + src + '"]'
    );
    if (existing) {
      // Tag may still be loading — poll for CANBMD rather than resolve-on-exist.
      afterTagReady();
      return;
    }

    var script = document.createElement("script");
    script.async = true;
    script.src = src;
    script.setAttribute("data-tpg-catholicadnet-loader", src);
    script.onload = function () {
      afterTagReady();
    };
    script.onerror = function () {
      delete loadedScripts[src];
      // Drop the failed tag too: a retry would otherwise adopt it as "existing"
      // and wait on a runtime that can never arrive.
      if (script.parentNode) script.parentNode.removeChild(script);
      reject(new Error("catholicadnet_script_failed"));
    };
    document.head.appendChild(script);
  });

  return loadedScripts[src];
}

function hostHasLayout(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return false;
  var rect = el.getBoundingClientRect();
  return rect.width >= 2 && rect.height >= 2;
}

/**
 * Activate once geometry + CANBMD are ready. Returns a cancel function so
 * remount / unmount do not leave polling timers or second activation cycles.
 *
 * @param {HTMLElement} ins
 * @param {string} unitId
 * @param {string} slotKey
 * @returns {function(): void}
 */
function activateCatholicAdnetUnit(ins, unitId, slotKey) {
  var noop = function () {};
  if (!ins || !unitId || typeof window === "undefined") return noop;

  var activated = false;
  var cancelled = false;
  var attempts = 0;
  var maxAttempts = 40;
  var scope = slotKey ? String(slotKey) : "slot";
  var timer = null;
  var rafId = null;

  function clearSchedulers() {
    if (timer != null) {
      window.clearInterval(timer);
      timer = null;
    }
    if (rafId != null && typeof window.cancelAnimationFrame === "function") {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function cancel() {
    cancelled = true;
    clearSchedulers();
  }

  function tryActivate() {
    if (cancelled || activated || !ins.parentNode) return true;
    if (!canbmdReady()) return false;
    if (!hostHasLayout(ins) && !hostHasLayout(ins.parentNode)) return false;

    var elemId =
      "BMD" +
      String(unitId) +
      "_" +
      scope +
      "_tpg" +
      String(Date.now()) +
      "_" +
      String(attempts);
    ins.id = elemId;
    ins.classList.add("CANDisplayAdDisplayed");
    // Microtask only — CANBMD is already confirmed ready by ensureScript / poll.
    window.setTimeout(function () {
      if (cancelled) return;
      if (typeof window.CANBMDRunADUnit !== "function") {
        ins.setAttribute("data-tpg-ad-state", "activation-failed");
        return;
      }
      // Bid start — the shared no-fill timer (displayCreativeWatcher) starts here.
      ins.setAttribute("data-tpg-ad-state", "requested");
      window.CANBMDRunADUnit(String(unitId), elemId);
    }, 0);
    activated = true;
    clearSchedulers();
    return true;
  }

  if (tryActivate()) return cancel;

  function startPoll() {
    if (cancelled || activated) return;
    timer = window.setInterval(function () {
      attempts += 1;
      if (tryActivate()) {
        clearSchedulers();
      } else if (attempts >= maxAttempts) {
        clearSchedulers();
        ins.setAttribute("data-tpg-ad-state", "activation-failed");
      }
    }, 50);
  }

  // Minimum layout wait: one paint when geometry is not ready yet.
  if (typeof window.requestAnimationFrame === "function") {
    rafId = window.requestAnimationFrame(function () {
      rafId = null;
      if (tryActivate()) return;
      startPoll();
    });
  } else {
    startPoll();
  }

  return cancel;
}

/**
 * Build a padding-top aspect box so height follows available width × unit ratio.
 * Landscape / MPU units fill the container width (inventory size is ratio only).
 * Tall/skyscraper units keep a native-width ceiling so a narrow unit is not
 * upscaled to a huge column height in a wide sidebar.
 *
 * @param {number} unitWidth
 * @param {number} unitHeight
 * @returns {{frame: HTMLElement, insHost: HTMLElement}}
 */
function buildResponsiveFrame(unitWidth, unitHeight) {
  var frame = document.createElement("div");
  frame.className = "catholicadnet-ad-frame";
  frame.style.width = "100%";

  if (unitWidth > 0 && unitHeight > 0) {
    frame.setAttribute("data-ad-width", String(unitWidth));
    frame.setAttribute("data-ad-height", String(unitHeight));
    // Portrait / skyscraper: do not upscale past native width.
    // Landscape / banner / MPU: fill available width (no inventory maxWidth).
    if (unitHeight > unitWidth * 1.25) {
      frame.style.maxWidth = String(unitWidth) + "px";
    } else {
      frame.style.maxWidth = "100%";
    }

    var sizer = document.createElement("div");
    sizer.className = "catholicadnet-ad-frame__sizer";
    sizer.setAttribute("aria-hidden", "true");
    sizer.style.display = "block";
    sizer.style.width = "100%";
    sizer.style.height = "0";
    sizer.style.paddingTop =
      ((unitHeight / unitWidth) * 100).toFixed(4) + "%";
    frame.appendChild(sizer);

    var mount = document.createElement("div");
    mount.className = "catholicadnet-ad-frame__mount";
    frame.appendChild(mount);
    return { frame: frame, insHost: mount };
  }

  return { frame: frame, insHost: frame };
}

/**
 * @param {HTMLElement} container
 * @param {object} network
 * @returns {HTMLElement|null}
 */
export function mountCatholicAdnetUnit(container, network) {
  if (!container || !network) return null;

  var markupClass = network.markup_class || "CANBMDDisplayAD";
  var unitId = network.provider_unit_id || "";
  if (!unitId) return null;

  if (typeof container._tpgCaCancel === "function") {
    container._tpgCaCancel();
    container._tpgCaCancel = null;
  }

  container.innerHTML = "";

  var unitWidth = parseInt(network.width, 10);
  var unitHeight = parseInt(network.height, 10);
  var built = buildResponsiveFrame(unitWidth, unitHeight);
  var frame = built.frame;
  var insHost = built.insHost;

  var ins = document.createElement("ins");
  ins.className = markupClass;
  ins.setAttribute("data-bmd-ad-unit", unitId);
  ins.style.display = "block";
  ins.style.width = "100%";
  ins.style.height = "100%";

  insHost.appendChild(ins);
  container.appendChild(frame);

  // Geometry is usually non-zero after append (padding-top sizer). tryActivate
  // runs immediately; one rAF only when the first sample is still collapsed.
  container._tpgCaCancel = activateCatholicAdnetUnit(
    ins,
    unitId,
    network.placement_key || network.placementKey || ""
  );

  return ins;
}

/**
 * @param {string} loaderUrl
 */
export function unloadCatholicAdnetScript(loaderUrl) {
  var src = normalizeLoaderUrl(loaderUrl);
  if (!src) return;
  var node = document.querySelector(
    'script[data-tpg-catholicadnet-loader="' + src + '"]'
  );
  if (node && node.parentNode) {
    node.parentNode.removeChild(node);
  }
  delete loadedScripts[src];
}
